import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { type PaginatedResponse, type User, userApi } from '@/api/admin/users';

interface UsersModalProfile {
  id: string;
  name: string;
}

interface UseProfileUsersModalReturn {
  isOpen: boolean;
  profile: UsersModalProfile | null;
  currentPage: number;
  usersData: PaginatedResponse<User> | undefined;
  isLoading: boolean;
  openModal: (profile: UsersModalProfile) => void;
  closeModal: () => void;
  handlePageChange: (page: number) => void;
}

export function useProfileUsersModal(): UseProfileUsersModalReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [profile, setProfile] = useState<UsersModalProfile | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const { data: usersData, isLoading } = useQuery({
    queryKey: ['profile-users', profile?.id, currentPage],
    queryFn: () => userApi.findByProfile(profile?.id!, { page: currentPage, limit: 10 }),
    enabled: !!profile?.id && isOpen,
  });

  const openModal = useCallback((selectedProfile: UsersModalProfile) => {
    setProfile(selectedProfile);
    setCurrentPage(1);
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setProfile(null);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  return {
    isOpen,
    profile,
    currentPage,
    usersData,
    isLoading,
    openModal,
    closeModal,
    handlePageChange,
  };
}
